import { useState, useEffect } from "react";
import { usePymbleApi } from "./usePymbleApi";
import { useQuery } from "@tanstack/react-query";

// const MY_IMAGE_ASSETS_END_POINT = `${api.datatownDb}/assets?subdir=`;
const MY_IMAGE_ASSETS_END_POINT = "/storage/assets/?subdir=";

export interface ImageAsset {
  id: string;
  img: string;
  content_type: string;
}

/**
 * Hook for separate loading of image assets
 * for components.
 * imageAssetUrl can be used to get a random url.
 */
export function useImageAssets(subdir: string) {
  const [imageAssetUrl, setImageAssetUrl] = useState<string>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [imageAssetUri, setImageAssetUri] = useState<string>();
  const [imageAsset, setImageAsset] = useState<ImageAsset>();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  const {
    isLoading,
    isError,
    data: imageAssets,
    error,
  } = useQuery({
    queryKey: ["image-assets", subdir],
    queryFn: () => callApi("GET", MY_IMAGE_ASSETS_END_POINT + subdir),
    enabled: isAuthenticated,
  });

  // Generate a random url
  const generateRandomUrl = async () => {
    // Get Random asset url to display
    let url = "";
    if (Array.isArray(imageAssets) && imageAssets.length) {
      const randomImage = () =>
        imageAssets[Math.floor(Math.random() * imageAssets.length)];
      const asset = randomImage();
      setImageAssetUrl(asset.img);
    }
    return url;
  };

  /**
   * Get a random image asset and return the ImageAsset json.
   *
   * @param options
   * @returns ImageAsset
   */
  const generateRandomAsset = () => {
    // Get Random asset json object to display
    let asset: ImageAsset = {
      id: "",
      img: "",
      content_type: "",
    };

    if (isLoading) {
      console.log(
        "Warning: image assets not loaded yet.  Empty asset will be returned."
      );
    }

    if (!isLoading && Array.isArray(imageAssets) && imageAssets.length) {
      const randomImage = () =>
        imageAssets[Math.floor(Math.random() * imageAssets.length)];
      const asset: ImageAsset = randomImage();
      setImageAsset(asset);
    }
    return asset;
  };

  // Generate a random uri
  const generateRandomUri = async () => {
    // Get Random asset uri to display
    let uri = "";
    if (Array.isArray(imageAssets) && imageAssets.length) {
      const randomImage = () =>
        imageAssets[Math.floor(Math.random() * imageAssets.length)];
      const asset: ImageAsset = randomImage();
      setImageAssetUri(asset.img);
    }
    return uri;
  };

  useEffect(() => {
    generateRandomUrl();
    generateRandomUri();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageAssets]);

  return {
    isLoading,
    isError,
    data: imageAssets,
    error,
    imageAssets,
    imageAssetsLoaded: !isLoading && !isError,
    imageAssetUrl,
    imageAsset,
    generateRandomUrl,
    generateRandomUri,
    generateRandomAsset,
  };
}
